import type { GetApiV1ThreeLetterCodePartnerAdminsClientsClientIdClientContracts200 } from '@fukuri-admins/apis/client/gen/model';
import { formatDate } from '@fukuri-admins/utils/common/date';
import { getContractPlanLink } from '~/utils/getLinks';
import type { ClientContractTermDetailPresentation } from '../../presentations';

type PresentationProps = React.ComponentProps<typeof ClientContractTermDetailPresentation>;

type Args = {
  /** API レスポンス（useFetch の data をそのまま受け取る） */
  data: GetApiV1ThreeLetterCodePartnerAdminsClientsClientIdClientContracts200;
  /** 企業契約期間 ID（Screen 層でパース・検証済みの数値） */
  termId: number;
};

type IConvertToPresentationProps = (args: Args) => PresentationProps;

/**
 * 企業契約期間データを ClientContractTermDetail の Presentation Props に変換する
 *
 * - data.client_contracts から termId に一致するレコードを絞り込む
 * - 見つからない場合は Error を throw する
 * - started_on: ISO 日付文字列（YYYY-MM-DD）→ 「yyyy/MM/dd」形式
 * - ended_on: ISO 日付文字列 → 「yyyy/MM/dd」形式 / null（無期限）→ 空文字列（空欄表示）
 * - contractPlanLink: contract_plan.id から契約プラン詳細画面へのパスを生成
 * - createdAtLabel / updatedAtLabel: UNIX秒（created_at / updated_at）を「YYYY/MM/DD HH:mm」形式に変換
 */
export const convertToPresentationProps: IConvertToPresentationProps = ({ data, termId }) => {
  const contractTerm = (data.client_contracts ?? []).find(c => c.id === termId);

  if (!contractTerm) {
    throw new Error(`企業契約期間 ID ${termId} が見つかりません`);
  }

  const startedOnLabel = formatDate({
    date: new Date(contractTerm.started_on),
    formatType: 'yyyy/MM/dd',
  });

  const endedOnLabel = contractTerm.ended_on
    ? formatDate({ date: new Date(contractTerm.ended_on), formatType: 'yyyy/MM/dd' })
    : '';

  const contractPlanLink = getContractPlanLink(contractTerm.contract_plan.id);

  const createdAtLabel = formatDate({
    date: new Date(contractTerm.created_at * 1000),
    formatType: 'yyyy/MM/dd HH:mm',
  });

  const updatedAtLabel = formatDate({
    date: new Date(contractTerm.updated_at * 1000),
    formatType: 'yyyy/MM/dd HH:mm',
  });

  return {
    clientName: contractTerm.client.name,
    contractPlanName: contractTerm.contract_plan.name,
    contractPlanLink,
    startedOnLabel,
    endedOnLabel,
    createdAtLabel,
    updatedAtLabel,
  };
};
